import React, { useEffect, useMemo, useState } from 'react';
import {
	Dialog,
	DialogContent,
	DialogTitle,
	IconButton,
	useMediaQuery,
	useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

import QuillesSponsorBanner from '../QuillesSponsorBanner';
import { formatNumber } from '../../services/csvDataService';
import { filterPlayerRounds } from '../../utils/playerStatsCharts';
import PlayerRoundsTable from './PlayerRoundsTable';
import PlayerStatsCharts from './PlayerStatsCharts';

export default function PlayerStatsModal({
	open,
	player,
	filters,
	sponsorBanner,
	onClose,
}) {
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down('md'));
	const hasCompetitionFilter = Boolean(
		filters?.zone && filters?.championship && filters?.category,
	);
	const [showAllRounds, setShowAllRounds] = useState(false);

	useEffect(() => {
		setShowAllRounds(false);
	}, [open, player?.licence]);

	const rounds = useMemo(() => {
		if (!player) return [];
		if (!hasCompetitionFilter || showAllRounds) return player.rounds;
		return filterPlayerRounds(player, filters);
	}, [player, filters, hasCompetitionFilter, showAllRounds]);

	const displayPlayer = useMemo(() => {
		if (!player) return null;
		if (rounds.length === player.rounds.length) return player;
		const totalQuilles = rounds.reduce((s, r) => s + r.quilles, 0);
		const totalParties = rounds.reduce((s, r) => s + r.parties, 0);
		return {
			...player,
			rounds,
			totalQuilles,
			totalParties,
			totalAverage: totalParties > 0 ? totalQuilles / totalParties : player.totalAverage,
		};
	}, [player, rounds]);

	if (!displayPlayer) {
		return (
			<Dialog open={open} onClose={onClose} maxWidth="md" fullWidth className="new-results-modal">
				<DialogTitle>Joueur</DialogTitle>
				<DialogContent>
					<p className="new-results-hint">Joueur introuvable.</p>
				</DialogContent>
			</Dialog>
		);
	}

	return (
		<Dialog
			open={open}
			onClose={onClose}
			maxWidth="md"
			fullWidth
			fullScreen={isMobile}
			className={`new-results-modal${isMobile ? ' new-results-modal--mobile' : ''}`}
			scroll="paper"
		>
			<DialogTitle className="new-results-modal-title">
				<div>
					<span className="new-results-modal-name">{displayPlayer.playerName}</span>
					<span className="new-results-modal-meta">
						Licence {displayPlayer.licence} · {displayPlayer.club}
						{displayPlayer.licenceLevel && ` · ${displayPlayer.licenceLevel}`}
					</span>
				</div>
				<div className="new-results-modal-title-actions">
					{hasCompetitionFilter && (
						<button
							type="button"
							className={`new-results-all-rounds-toggle${showAllRounds ? ' new-results-all-rounds-toggle--on' : ''}`}
							aria-pressed={showAllRounds}
							onClick={() => setShowAllRounds((value) => !value)}
						>
							Toutes manches
						</button>
					)}
					<IconButton aria-label="Fermer" onClick={onClose} size="small">
						<CloseIcon />
					</IconButton>
				</div>
			</DialogTitle>

			<DialogContent dividers className="new-results-modal-body">
				<div className="new-results-modal-stack">
					<section className="new-results-modal-section">
						<div className="new-results-stats-row">
							<div className="new-results-stat">
								<div className="new-results-stat-value">{formatNumber(displayPlayer.totalQuilles)}</div>
								<div className="new-results-stat-label">Total quilles</div>
							</div>
							<div className="new-results-stat">
								<div className="new-results-stat-value">{formatNumber(displayPlayer.totalParties)}</div>
								<div className="new-results-stat-label">Parties</div>
							</div>
							<div className="new-results-stat">
								<div className="new-results-stat-value">{formatNumber(displayPlayer.totalAverage, 2)}</div>
								<div className="new-results-stat-label">Moyenne</div>
							</div>
						</div>
						{sponsorBanner && (
							<div className="new-results-modal-sponsor">
								<QuillesSponsorBanner banner={sponsorBanner} />
							</div>
						)}
					</section>

					<section className="new-results-modal-section">
						<PlayerStatsCharts
							rounds={rounds}
							overallAverage={displayPlayer.totalAverage}
						/>
					</section>

					{rounds.length > 0 ? (
						<section className="new-results-modal-section new-results-modal-section--table">
							<PlayerRoundsTable
								rounds={rounds}
								totalParties={displayPlayer.totalParties}
								totalQuilles={displayPlayer.totalQuilles}
							/>
						</section>
					) : (
						<p className="new-results-hint">Aucune manche pour les critères sélectionnés.</p>
					)}
				</div>
			</DialogContent>
		</Dialog>
	);
}
